import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Icon } from '@iconify/react';
import { getCustomImageUrl } from '../utils/imageProxy';

interface ProjectSlideshowProps {
  images: string[];
  title: string;
  autoplay?: boolean;
  interval?: number;
  showThumbnails?: boolean;
  className?: string;
}

export function ProjectSlideshow({ 
  images, 
  title, 
  autoplay = true, 
  interval = 5000,
  showThumbnails = true,
  className = ""
}: ProjectSlideshowProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(autoplay);
  const [imageLoadErrors, setImageLoadErrors] = useState<Set<number>>(new Set());

  // Filter out images that failed to load
  const validImages = images.filter((_, index) => !imageLoadErrors.has(index));
  const validCurrentIndex = Math.min(currentIndex, validImages.length - 1);

  useEffect(() => {
    if (!isPlaying || validImages.length <= 1) return;

    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % validImages.length);
    }, interval);

    return () => clearInterval(timer);
  }, [isPlaying, validImages.length, interval]);

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
  };

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + validImages.length) % validImages.length);
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % validImages.length);
  };

  const togglePlayback = () => {
    setIsPlaying(!isPlaying);
  };

  const handleImageError = (index: number) => {
    setImageLoadErrors(prev => new Set([...prev, index]));
  };

  if (!images || images.length === 0) {
    return (
      <div className={`relative aspect-video bg-gray-200 dark:bg-gray-700 rounded-lg flex items-center justify-center ${className}`}>
        <div className="text-center text-gray-500 dark:text-gray-400">
          <Icon icon="lucide:image-off" className="w-12 h-12 mx-auto mb-2" />
          <p>No images available</p>
        </div>
      </div>
    );
  }

  if (validImages.length === 0) {
    return (
      <div className={`relative aspect-video bg-gray-200 dark:bg-gray-700 rounded-lg flex items-center justify-center ${className}`}>
        <div className="text-center text-gray-500 dark:text-gray-400">
          <Icon icon="lucide:image-x" className="w-12 h-12 mx-auto mb-2" />
          <p>Failed to load images</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`relative ${className}`}>
      {/* Main slideshow container */}
      <div className="relative aspect-video rounded-lg overflow-hidden bg-gray-900">
        <AnimatePresence mode="wait">
          <motion.div
            key={validCurrentIndex}
            initial={{ opacity: 0, x: 300 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -300 }}
            transition={{ duration: 0.5, ease: "easeInOut" }}
            className="absolute inset-0"
          >
            <img
              src={getCustomImageUrl(validImages[validCurrentIndex], { width: 1000, quality: 90, format: 'webp' })}
              alt={`${title} - Image ${validCurrentIndex + 1}`}
              className="w-full h-full object-cover"
              onError={() => handleImageError(validCurrentIndex)}
            />
          </motion.div>
        </AnimatePresence>

        {/* Overlay gradient for better text visibility */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />

        {/* Navigation arrows */}
        {validImages.length > 1 && (
          <>
            <button
              onClick={goToPrevious}
              className="absolute left-4 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors backdrop-blur-sm"
              aria-label="Previous image"
            >
              <Icon icon="lucide:chevron-left" width="24" height="24" />
            </button>
            <button
              onClick={goToNext}
              className="absolute right-4 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors backdrop-blur-sm"
              aria-label="Next image"
            >
              <Icon icon="lucide:chevron-right" width="24" height="24" />
            </button>
          </>
        )}

        {/* Play/Pause button */}
        {validImages.length > 1 && (
          <button
            onClick={togglePlayback}
            className="absolute top-4 right-4 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors backdrop-blur-sm"
            aria-label={isPlaying ? "Pause slideshow" : "Play slideshow"}
          >
            <Icon 
              icon={isPlaying ? "lucide:pause" : "lucide:play"} 
              width="20" 
              height="20" 
            />
          </button>
        )}

        {/* Image counter */}
        <div className="absolute bottom-4 left-4 px-3 py-1 rounded-full bg-black/50 text-white text-sm backdrop-blur-sm">
          {validCurrentIndex + 1} / {validImages.length}
        </div>

        {/* Dots indicator */}
        {validImages.length > 1 && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
            {validImages.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === validCurrentIndex 
                    ? 'bg-white scale-125' 
                    : 'bg-white/50 hover:bg-white/75'
                }`}
                aria-label={`Go to image ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>

      {/* Thumbnails */}
      {showThumbnails && validImages.length > 1 && (
        <div className="mt-4 flex gap-2 overflow-x-auto pb-2">
          {validImages.map((image, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`flex-shrink-0 w-20 h-12 rounded-lg overflow-hidden border-2 transition-all ${
                index === validCurrentIndex
                  ? 'border-primary-500 ring-2 ring-primary-500/30'
                  : 'border-gray-300 dark:border-gray-600 hover:border-primary-300 dark:hover:border-primary-400'
              }`}
            >
              <img
                src={getCustomImageUrl(image, { width: 200, quality: 80, format: 'webp' })}
                alt={`${title} thumbnail ${index + 1}`}
                className="w-full h-full object-cover"
                onError={() => handleImageError(index)}
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
